/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  signal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/** What the preview pane can do with a file, decided from its extension. */
export type PreviewKind = 'pdf' | 'image' | 'other' | 'empty';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp']);

/** Thumbnails beyond this are skipped — the strip is for jumping, not for browsing a book. */
const MAX_THUMBS = 100;

const MIN_ZOOM = 25;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;

function extensionOf(name: string): string {
  return (name.split('.').pop() ?? '').toLowerCase();
}

/**
 * How many pages a PDF has, without keeping the document around.
 *
 * Used for the queue table's page column: the file is parsed once in the
 * background and the count is patched onto its row — a cheap structure read,
 * no page is drawn.
 */
export async function countPdfPages(file: File): Promise<number> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;
  const doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  const pages = doc.numPages;
  await doc.destroy();
  return pages;
}

/**
 * Renders a not-yet-uploaded `File` for checking before it is committed.
 *
 * PDFs are drawn by pdf.js — already in the bundle for the document viewer —
 * onto a canvas, with a thumbnail rail for jumping between pages. Images are
 * shown from an object URL. Everything else (Word, Excel, PowerPoint, text)
 * has no local renderer and gets a placeholder card; the file still uploads
 * normally — preview is a courtesy, not a gate.
 */
@Component({
  selector: 'app-local-file-preview',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './local-file-preview.component.html',
  styleUrls: ['./local-file-preview.component.scss']
})
export class LocalFilePreviewComponent implements OnChanges, OnDestroy, AfterViewInit {
  /** The queued file to draw. Null shows the "pick a file" placeholder. */
  @Input() file: File | null = null;

  /**
   * Reported once the file's page count is known — PDFs by parsing, images are 1.
   * The file is echoed back so a fast switch cannot stamp one file's count onto another.
   */
  @Output() pagesKnown = new EventEmitter<{ file: File; pages: number }>();

  @ViewChild('stageWrap') private readonly stageWrap?: ElementRef<HTMLElement>;

  readonly kind = signal<PreviewKind>('empty');
  readonly loading = signal(false);
  readonly failed = signal(false);

  readonly page = signal(1);
  readonly pageCount = signal(0);
  /** Percent: 100 = natural size. */
  readonly zoom = signal(100);
  /** Fit-width mode recomputes the scale from the viewer's width on each resize. */
  readonly fitWidth = signal(true);

  readonly imageUrl = signal<string | null>(null);
  /** Thumbnail images (data URLs) for the rail, rendered lazily after the page. */
  readonly thumbs = signal<string[]>([]);

  private pdfjs?: typeof import('pdfjs-dist');
  private doc?: PDFDocumentProxy;
  /** Natural width of the current page at scale 1, for fit-width math. */
  private naturalWidth = 0;
  /** Bumped on every render request so a stale render cannot paint over a newer one. */
  private renderToken = 0;
  private resizeObserver?: ResizeObserver;
  private objectUrl?: string;

  ngAfterViewInit(): void {
    this.observeStage(this.stageWrap?.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['file']) {
      void this.load();
    }
  }

  ngOnDestroy(): void {
    this.renderToken++;
    this.resizeObserver?.disconnect();
    void this.doc?.destroy();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  // ---- loading ---------------------------------------------------------------

  private async load(): Promise<void> {
    this.renderToken++;
    this.resizeObserver?.disconnect();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
    const previous = this.doc;
    this.doc = undefined;
    void previous?.destroy();
    this.thumbs.set([]);
    this.imageUrl.set(null);

    const file = this.file;
    if (!file) {
      this.kind.set('empty');
      this.pageCount.set(0);
      return;
    }

    const ext = extensionOf(file.name);
    const kind: PreviewKind = ext === 'pdf' ? 'pdf' : IMAGE_EXTENSIONS.has(ext) ? 'image' : 'other';
    this.kind.set(kind);
    this.page.set(1);
    this.pageCount.set(kind === 'image' ? 1 : 0);
    this.failed.set(false);

    if (kind === 'image') {
      this.objectUrl = URL.createObjectURL(file);
      this.imageUrl.set(this.objectUrl);
      this.pagesKnown.emit({ file, pages: 1 });
      return;
    }
    if (kind !== 'pdf') {
      return;
    }

    this.loading.set(true);
    try {
      this.pdfjs = this.pdfjs ?? (await import('pdfjs-dist'));
      this.pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${this.pdfjs.version}`;
      const doc = await this.pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
      if (this.file !== file) {
        void doc.destroy();
        return;
      }
      this.doc = doc;
      this.pageCount.set(doc.numPages);
      this.pagesKnown.emit({ file, pages: doc.numPages });
      await this.renderPage();
      void this.renderThumbs();
    } catch {
      this.failed.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  // ---- pdf rendering ----------------------------------------------------------

  /** The scale the page is drawn at: fit-width uses the stage's live width. */
  private drawScale(): number {
    if (this.fitWidth() && this.naturalWidth > 0) {
      const wrap = this.stageWrap?.nativeElement;
      const width = wrap ? wrap.clientWidth - 32 : 0;
      if (width > 0) {
        return width / this.naturalWidth;
      }
    }
    return this.zoom() / 100;
  }

  private async renderPage(): Promise<void> {
    const token = ++this.renderToken;
    const wrap = this.stageWrap?.nativeElement;
    if (!this.doc || !wrap) {
      return;
    }

    const page = await this.doc.getPage(this.page());
    if (token !== this.renderToken) {
      return;
    }
    const natural = page.getViewport({ scale: 1 });
    this.naturalWidth = natural.width;
    const scale = this.drawScale();
    const viewport = page.getViewport({ scale });

    wrap.innerHTML = '';
    const canvas = document.createElement('canvas');
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.ceil(viewport.width * ratio);
    canvas.height = Math.ceil(viewport.height * ratio);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.className = 'aca-localprev__page';
    wrap.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (ctx) {
      await page.render({ canvasContext: ctx, viewport: page.getViewport({ scale: scale * ratio }) }).promise;
    }
  }

  /** Draws the rail's small page images, one at a time so a big file never stalls the tab. */
  private async renderThumbs(): Promise<void> {
    const doc = this.doc;
    if (!doc) {
      return;
    }
    const count = Math.min(doc.numPages, MAX_THUMBS);
    const out: string[] = [];
    for (let i = 1; i <= count; i++) {
      if (this.doc !== doc) {
        return;
      }
      try {
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = 96 / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          await page.render({ canvasContext: ctx, viewport }).promise;
          out.push(canvas.toDataURL('image/png'));
          this.thumbs.set([...out]);
        }
      } catch {
        out.push('');
      }
    }
  }

  // ---- toolbar actions ---------------------------------------------------------

  goToPage(target: number): void {
    const max = this.pageCount() || 1;
    const next = Math.min(Math.max(Math.round(target) || 1, 1), max);
    if (next === this.page()) {
      return;
    }
    this.page.set(next);
    if (this.kind() === 'pdf') {
      void this.renderPage();
    }
  }

  onPageInput(event: Event): void {
    this.goToPage(Number((event.target as HTMLInputElement).value));
  }

  zoomIn(): void {
    this.setZoom(this.zoom() + ZOOM_STEP);
  }

  zoomOut(): void {
    this.setZoom(this.zoom() - ZOOM_STEP);
  }

  resetZoom(): void {
    this.fitWidth.set(false);
    this.setZoom(100);
  }

  toggleFitWidth(): void {
    this.fitWidth.update((v) => !v);
    if (this.fitWidth()) {
      void this.renderPage();
    }
  }

  /** Recomputes fit-width scale when the pane resizes. */
  onStageResize(): void {
    if (this.fitWidth() && this.kind() === 'pdf') {
      void this.renderPage();
    }
  }

  private setZoom(value: number): void {
    this.fitWidth.set(false);
    this.zoom.set(Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM));
    if (this.kind() === 'pdf') {
      void this.renderPage();
    }
  }

  /** Wires a resize observer once the stage exists — kept out of load() so the DOM is there. */
  observeStage(el: HTMLElement | undefined): void {
    this.resizeObserver?.disconnect();
    if (!el) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.onStageResize());
    this.resizeObserver.observe(el);
  }

  /** `transform: scale()` for the image branch — zoom without re-decoding. */
  imageScale(): string {
    return `scale(${this.zoom() / 100})`;
  }
}
